import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  QuestionnaireWithSections,
  CriticalSectionsItem,
  ProcessResultItem,
  QueryMode,
  ProcessStatusQueryParams,
} from '../types'
import { ScheduleService } from '../../../../../../../service/schedule'
import { httpFetch } from '../../../../../../../utils/crud'
import { EventQuery } from '../../../../../../../hooks/emitter/types'

export type SortOrder = 'desc' | 'asc'

interface UseProcessStatusProps {
  queryParams: ProcessStatusQueryParams
  queryMode: QueryMode
  scoreMap: Map<string, number>
  enabled: boolean
}

interface UseProcessStatusReturn {
  searchLeft: string
  setSearchLeft: (value: string) => void
  searchGlobal: string
  setSearchGlobal: (value: string) => void
  rangeValue: [number, number]
  setRangeValue: (value: [number, number]) => void
  appliedRangeValue: [number, number]
  applyRange: () => void
  isRangeDirty: boolean
  sortOrder: SortOrder
  setSortOrder: (order: SortOrder) => void
  expandedLeft: Set<string>
  expandedRight: Set<string>
  toggleLeft: (id: string) => void
  toggleRight: (id: string) => void
  pageLeft: number
  setPageLeft: (page: number) => void
  pageRight: number
  setPageRight: (page: number) => void
  paginatedLeft: QuestionnaireWithSections[]
  totalPagesLeft: number
  totalItemsLeft: number
  paginatedRight: QuestionnaireWithSections[]
  totalPagesRight: number
  totalItemsRight: number
  criticalLoading: boolean
  processLoading: boolean
  fetchCriticalSections: (range?: [number, number]) => Promise<void>
  fetchProcessResult: (order?: SortOrder) => Promise<void>
}

const ITEMS_PER_PAGE = 8

const getScoreColor = (score: number): string => {
  if (score >= 81) return '#22c55e'
  if (score >= 61) return '#f59e0b'
  return '#ef4444'
}

const getItemName = (
  item: CriticalSectionsItem | ProcessResultItem,
  queryMode: QueryMode
): string => {
  if (queryMode === 'many-units-one-questionnaire') {
    return item.storeName
  }
  return item.questionnaireName
}

const getCompositeId = (item: CriticalSectionsItem | ProcessResultItem): string => {
  return `${item.storeId}-${item.questionnaireId}`
}

const transformCriticalSections = (
  items: CriticalSectionsItem[],
  queryMode: QueryMode
): QuestionnaireWithSections[] => {
  return items.map((item) => {
    const scores = item.criticalSections.map((s) => s.percentage)
    const avgScore =
      scores.length > 0
        ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
        : 0

    return {
      id: getCompositeId(item),
      name: getItemName(item, queryMode),
      score: avgScore,
      sections: item.criticalSections.map((section) => ({
        id: String(section.sectionId),
        name: section.sectionName,
        score: section.percentage,
        questions: section.questions.map((q) => ({
          id: String(q.questionId),
          name: q.questionTitle,
          score: q.percentage,
        })),
      })),
    }
  })
}

const transformProcessResult = (
  items: ProcessResultItem[],
  queryMode: QueryMode,
  scoreMap: Map<string, number>
): QuestionnaireWithSections[] => {
  return items.map((item) => {
    let scoreId: string
    if (queryMode === 'many-units-one-questionnaire') {
      scoreId = String(item.storeId)
    } else {
      scoreId = String(item.questionnaireMainId)
    }

    let globalScore = scoreMap.get(scoreId)

    if (globalScore === undefined && queryMode !== 'many-units-one-questionnaire') {
      globalScore = scoreMap.get(String(item.questionnaireId))
    }

    const finalScore = globalScore ?? 0

    return {
      id: getCompositeId(item),
      name: getItemName(item, queryMode),
      score: finalScore,
      sections: item.sections.map((section) => ({
        id: String(section.sectionId),
        name: section.sectionName,
        score: section.percentage,
        questions: [],
      })),
    }
  })
}

const useProcessStatus = ({
  queryParams,
  queryMode,
  scoreMap,
  enabled,
}: UseProcessStatusProps): UseProcessStatusReturn => {
  const [searchLeft, setSearchLeft] = useState('')
  const [searchGlobal, setSearchGlobal] = useState('')
  const [rangeValue, setRangeValue] = useState<[number, number]>([0, 60])
  const [appliedRangeValue, setAppliedRangeValue] = useState<[number, number]>([0, 60])
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
  const [expandedLeft, setExpandedLeft] = useState<Set<string>>(new Set())
  const [expandedRight, setExpandedRight] = useState<Set<string>>(new Set())
  const lastAutoExpandedLeftId = useRef<string | null>(null)
  const [pageLeft, setPageLeft] = useState(1)
  const [pageRight, setPageRight] = useState(1)

  const baseParams = useMemo(
    () => ({
      store_ids: queryParams.storeIds,
      questionnaire_ids: queryParams.questionnaireIds,
      company_id: queryParams.companyId || undefined,
      date_init: queryParams.dateInit || undefined,
      date_end: queryParams.dateEnd || undefined,
      year: queryParams.year,
      weeks: queryParams.weeks || undefined,
      division_ids: queryParams.divisionIds || undefined,
      zone_ids: queryParams.zoneIds || undefined,
      user_ids: queryParams.userIds || undefined,
      role_ids: queryParams.roleIds || undefined,
    }),
    [queryParams]
  )

  /* ───────────────────────────
     Critical Sections (React Query)
     ─────────────────────────── */
  const criticalQuery = useQuery({
    queryKey: [
      EventQuery.store_performance_critical_sections,
      baseParams,
      pageLeft,
      appliedRangeValue,
    ],
    queryFn: async () => {
      const response = await ScheduleService(httpFetch).getCriticalSections({
        ...baseParams,
        page: pageLeft,
        size: ITEMS_PER_PAGE,
        order: 'ASC',
        range_critical_init: appliedRangeValue[0],
        range_critical_end: appliedRangeValue[1],
      })

      return {
        items: transformCriticalSections(response.items ?? [], queryMode),
        totalPages: response.totalPages ?? 1,
        totalItems: response.totalItems ?? 0,
      }
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  /* ───────────────────────────
     Process Result (React Query)
     ─────────────────────────── */
  const processQuery = useQuery({
    queryKey: [
      EventQuery.store_performance_process_result,
      baseParams,
      pageRight,
    ],
    queryFn: async () => {
      const response = await ScheduleService(httpFetch).getProcessResult({
        ...baseParams,
        page: pageRight,
        size: ITEMS_PER_PAGE,
        order: 'DESC',
      })

      return {
        items: transformProcessResult(
          response.items ?? [],
          queryMode,
          scoreMap
        ),
        totalPages: response.totalPages ?? 1,
        totalItems: response.totalItems ?? 0,
      }
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const criticalData = criticalQuery.data?.items ?? []
  const criticalTotalPages = criticalQuery.data?.totalPages ?? 1
  const criticalTotalItems = criticalQuery.data?.totalItems ?? 0

  const processData = processQuery.data?.items ?? []
  const processTotalPages = processQuery.data?.totalPages ?? 1
  const processTotalItems = processQuery.data?.totalItems ?? 0

  const criticalLoading = criticalQuery.isLoading || criticalQuery.isFetching
  const processLoading = processQuery.isLoading || processQuery.isFetching

  /* ───────────────────────────
     Expandir primer ítem al cargar datos críticos
     (solo cuando cambia el primer item del array, no cuando el usuario colapsa)
     ─────────────────────────── */
  useEffect(() => {
    if (criticalData.length > 0 && criticalData[0].id !== lastAutoExpandedLeftId.current) {
      setExpandedLeft(new Set([criticalData[0].id]))
      lastAutoExpandedLeftId.current = criticalData[0].id
    }
  }, [criticalData])

  /* ───────────────────────────
     Expandir primer ítem al cargar datos de proceso
     ─────────────────────────── */
  useEffect(() => {
    if (processData.length > 0 && expandedRight.size === 0) {
      setExpandedRight(new Set([processData[0].id]))
    }
  }, [processData, expandedRight.size])

  const filteredLeft = useMemo(() => {
    if (!searchLeft.trim()) return criticalData
    const term = searchLeft.toLowerCase()
    return criticalData.filter(
      (q) =>
        q.name.toLowerCase().includes(term) ||
        q.sections.some(
          (s) =>
            s.name.toLowerCase().includes(term) ||
            s.questions.some((ques) => ques.name.toLowerCase().includes(term))
        )
    )
  }, [criticalData, searchLeft])

  const paginatedLeft = filteredLeft

  const filteredRight = useMemo(() => {
    if (!searchGlobal.trim()) return processData
    const term = searchGlobal.toLowerCase()
    return processData.filter(
      (q) =>
        q.name.toLowerCase().includes(term) ||
        q.sections.some((s) => s.name.toLowerCase().includes(term))
    )
  }, [processData, searchGlobal])

  // Ordenar client-side según el toggle de ordenamiento (padres e hijos)
  const sortedRight = useMemo(() => {
    const items = [...filteredRight]
    return items.sort((a, b) =>
      sortOrder === 'desc' ? b.score - a.score : a.score - b.score
    )
  }, [filteredRight, sortOrder])

  const paginatedRight = sortedRight

  // Al cambiar el ordenamiento, conservar los ítems expandidos si siguen visibles;
  // si ninguno sobrevive, expandir el primero del nuevo orden
  useEffect(() => {
    if (sortedRight.length === 0) {
      setExpandedRight(new Set())
      return
    }
    const visibleIds = new Set(sortedRight.map((item) => item.id))
    const surviving = [...expandedRight].filter((id) => visibleIds.has(id))
    if (surviving.length > 0) {
      setExpandedRight(new Set(surviving))
    } else {
      setExpandedRight(new Set([sortedRight[0].id]))
    }
  }, [sortOrder, sortedRight])

  const toggleLeft = useCallback((id: string) => {
    setExpandedLeft((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const toggleRight = useCallback((id: string) => {
    setExpandedRight((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const handleSetRangeValue = useCallback((value: [number, number]) => {
    setRangeValue(value)
  }, [])

  const applyRange = useCallback(() => {
    setAppliedRangeValue(rangeValue)
    setPageLeft(1)
  }, [rangeValue])

  const handleSetSortOrder = useCallback((order: SortOrder) => {
    setSortOrder(order)
  }, [])

  const handleSetPageLeft = useCallback((page: number) => {
    setPageLeft(page)
  }, [])

  const handleSetPageRight = useCallback((page: number) => {
    setPageRight(page)
  }, [])

  /**
   * Se mantienen por compatibilidad con consumidores existentes.
   * Con React Query el cacheo y re-fetch son automáticos; estas funciones
   * ya no disparan peticiones manuales.
   */
  const fetchCriticalSections = useCallback(
    async (_range?: [number, number]) => {
      // El range ya está en el queryKey; React Query invalida automáticamente.
      // Si se necesita forzar un re-fetch manual, usar queryClient.invalidateQueries.
    },
    []
  )

  const fetchProcessResult = useCallback(
    async (_order?: SortOrder) => {
      // El ordenamiento es client-side; no requiere re-fetch.
    },
    []
  )

  return {
    searchLeft,
    setSearchLeft,
    searchGlobal,
    setSearchGlobal,
    rangeValue,
    setRangeValue: handleSetRangeValue,
    appliedRangeValue,
    applyRange,
    isRangeDirty: rangeValue[0] !== appliedRangeValue[0] || rangeValue[1] !== appliedRangeValue[1],
    sortOrder,
    setSortOrder: handleSetSortOrder,
    expandedLeft,
    expandedRight,
    toggleLeft,
    toggleRight,
    pageLeft,
    setPageLeft: handleSetPageLeft,
    pageRight,
    setPageRight: handleSetPageRight,
    paginatedLeft,
    totalPagesLeft: criticalTotalPages,
    totalItemsLeft: criticalTotalItems,
    paginatedRight,
    totalPagesRight: processTotalPages,
    totalItemsRight: processTotalItems,
    criticalLoading,
    processLoading,
    fetchCriticalSections,
    fetchProcessResult,
  }
}

export { useProcessStatus, getScoreColor, ITEMS_PER_PAGE }
